import type { CardPair } from '../game/types';
import { normalizePairs } from '../game/rules';

export class MissingKeyError extends Error {
  constructor() {
    super('MISSING_KEY');
    this.name = 'MissingKeyError';
  }
}
export class LocationUnsupportedError extends Error {
  constructor() {
    super('LOCATION_UNSUPPORTED');
    this.name = 'LocationUnsupportedError';
  }
}
export class KeyForbiddenError extends Error {
  constructor() {
    super('KEY_ERROR_403');
    this.name = 'KeyForbiddenError';
  }
}
export class GeminiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GeminiError';
  }
}

const MODELS_TO_TRY = ['gemini-2.5-flash', 'gemini-2.5-pro', 'gemini-1.5-flash'] as const;

type GeminiPart = { text?: string };
type GeminiCandidate = { content?: { parts?: GeminiPart[] } };
type GeminiResponse = { candidates?: GeminiCandidate[]; error?: { message?: string } };

export async function callGemini(
  prompt: string,
  apiKey: string,
  systemInstruction: string = 'You are a helpful assistant.',
  signal?: AbortSignal,
): Promise<string> {
  const key = apiKey.trim();
  if (!key) throw new MissingKeyError();

  let lastErr: Error | null = null;
  for (const model of MODELS_TO_TRY) {
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${encodeURIComponent(key)}`;
    const payload = {
      contents: [{ parts: [{ text: prompt }] }],
      system_instruction: { parts: [{ text: systemInstruction }] },
    };
    try {
      const init: RequestInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      };
      if (signal) init.signal = signal;
      const res = await fetch(url, init);
      if (!res.ok) {
        let msg = res.statusText;
        try {
          const errData = (await res.json()) as GeminiResponse;
          msg = errData.error?.message ?? msg;
        } catch {
          /* noop */
        }
        if (msg.includes('User location is not supported')) throw new LocationUnsupportedError();
        if (res.status === 403) throw new KeyForbiddenError();
        lastErr = new GeminiError(`API Error ${res.status}: ${msg}`);
        continue;
      }
      const data = (await res.json()) as GeminiResponse;
      return data.candidates?.[0]?.content?.parts?.[0]?.text ?? '';
    } catch (e) {
      if (e instanceof LocationUnsupportedError || e instanceof KeyForbiddenError) throw e;
      lastErr = e instanceof Error ? e : new GeminiError(String(e));
    }
  }
  throw lastErr ?? new GeminiError('All models failed');
}

export async function generateDeck(
  topic: string,
  apiKey: string,
  signal?: AbortSignal,
): Promise<CardPair[]> {
  const sys = 'You are a helpful assistant. Generate 8 pairs of Question and Answer.';
  const user = `Generate 8 pairs of Question/Answer for topic: "${topic}". Return valid JSON Array like [{"q":"Q","a":"A"}]. No markdown.`;
  const raw = await callGemini(user, apiKey, sys, signal);
  let txt = raw.replace(/```json|```/g, '').trim();
  const match = txt.match(/\[[\s\S]*\]/);
  if (match) txt = match[0];
  const parsed = JSON.parse(txt) as unknown;
  return normalizePairs(parsed);
}

export async function generateCommentary(
  winnerNames: string[],
  winnerFruits: number,
  apiKey: string,
  signal?: AbortSignal,
): Promise<string> {
  const stats = `Winner: ${winnerNames.join('&')} (${winnerFruits} fruits).`;
  const prompt = `Write a funny sports news report (Traditional Chinese) about "Farmer Fruit Snatching". Stats: ${stats}. < 100 words.`;
  return callGemini(prompt, apiKey, 'You are a helpful assistant.', signal);
}

export async function testConnection(apiKey: string): Promise<void> {
  await callGemini('Hello', apiKey);
}
